export function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value);
  }

  return [];
}

function normalizeString(value) {
  return String(value ?? '').trim();
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return null;
}

function normalizeOptionValue(option) {
  if (option && typeof option === 'object') {
    return normalizeString(option.value ?? option.label ?? '');
  }

  return normalizeString(option);
}

function normalizeOptions(options) {
  return normalizeList(options)
    .map((item) => {
      if (item && typeof item === 'object') {
        const label = normalizeString(item.label ?? item.value ?? '');
        const value = normalizeString(item.value ?? item.label ?? '');
        if (!label && !value) {
          return null;
        }
        return { label: label || value, value: value || label };
      }

      const value = normalizeString(item);
      if (!value) {
        return null;
      }

      return { label: value, value };
    })
    .filter(Boolean);
}

function normalizeDependency(dependency) {
  const source = dependency && typeof dependency === 'object' ? dependency : {};
  const impacts = normalizeList(source.impacts ?? source.values)
    .map(normalizeOptionValue)
    .filter(Boolean);

  return {
    sourceFieldId: normalizeString(
      source.fieldIdRelated
      || source.fieldId
      || source.relatedFieldId
      || '',
    ),
    impacts,
    checked: normalizeBoolean(source.checked),
  };
}

function buildAliases(field, fallbackKey) {
  const source = field && typeof field === 'object' ? field : {};
  const aliases = [
    normalizeString(source.id),
    normalizeString(source.uid),
    normalizeString(source.fieldId),
    normalizeString(source.sourceId),
  ].filter(Boolean);

  if (aliases.length === 0) {
    return [fallbackKey];
  }

  return Array.from(new Set(aliases));
}

export function buildRuntimeFields(fields) {
  return normalizeList(fields).map((field, index) => {
    const source = field && typeof field === 'object' ? field : {};
    const fallbackKey = `field-${index}`;
    const aliases = buildAliases(source, fallbackKey);
    const key = aliases[0] || fallbackKey;

    return {
      key,
      aliases,
      name: normalizeString(source.name),
      type: normalizeString(source.type) || 'text',
      placeholder: normalizeString(source.placeholder),
      required: Boolean(source.required),
      visible: source.visible !== false,
      options: normalizeOptions(source.options),
      dependencies: normalizeList(source.dependencies).map(normalizeDependency),
    };
  });
}

export function buildAliasToKeyMap(runtimeFields) {
  const aliasToKey = {};

  runtimeFields.forEach((field) => {
    field.aliases.forEach((alias) => {
      aliasToKey[alias] = field.key;
    });
    aliasToKey[field.key] = field.key;
  });

  return aliasToKey;
}

function isDependencySatisfied(dependency, runtimeFieldsByKey, aliasToKey, values) {
  if (!dependency.sourceFieldId) {
    return true;
  }

  const sourceKey = aliasToKey[dependency.sourceFieldId] || '';
  if (!sourceKey) {
    return false;
  }

  const sourceField = runtimeFieldsByKey[sourceKey];
  if (!sourceField) {
    return false;
  }

  if (sourceField.type === 'checkbox') {
    if (dependency.checked === null) {
      return true;
    }

    return Boolean(values[sourceKey]) === dependency.checked;
  }

  if (dependency.impacts.length === 0) {
    return true;
  }

  const sourceValue = normalizeString(values[sourceKey]);
  return dependency.impacts.includes(sourceValue);
}

export function computeVisibilityMap(runtimeFields, values) {
  const aliasToKey = buildAliasToKeyMap(runtimeFields);
  const runtimeFieldsByKey = {};

  runtimeFields.forEach((field) => {
    runtimeFieldsByKey[field.key] = field;
  });

  const map = {};
  runtimeFields.forEach((field) => {
    const hasDependencies = field.dependencies.length > 0;
    const dependenciesSatisfied = field.dependencies.every((dependency) => (
      isDependencySatisfied(dependency, runtimeFieldsByKey, aliasToKey, values)
    ));

    map[field.key] = hasDependencies ? dependenciesSatisfied : field.visible;
  });

  return map;
}

export function cleanupHiddenValues(values, visibilityMap) {
  const nextValues = { ...values };
  let changed = false;

  Object.keys(nextValues).forEach((key) => {
    if (!visibilityMap[key]) {
      delete nextValues[key];
      changed = true;
    }
  });

  return changed ? nextValues : values;
}

export function normalizeCountryOptions(countries) {
  return normalizeList(countries)
    .map((item) => {
      if (item && typeof item === 'object') {
        const label = normalizeString(item.label ?? item.value ?? '');
        const value = normalizeString(item.value ?? item.label ?? '');
        if (!label || !value) {
          return null;
        }
        return { label, value };
      }

      const value = normalizeString(item);
      if (!value) {
        return null;
      }

      return { label: value, value };
    })
    .filter(Boolean);
}
